"use client";

import { notify, confirmDialog } from "@/components/ui/dialogs";
import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  User, 
  Shield, 
  Lock, 
  Mail,
  Phone,
  Eye,
  EyeOff,
  Bell,
  Camera,
  Trash2,
  Loader2,
  Globe,
  KeyRound,
  Link2,
  CheckCircle2} from "lucide-react";
import { get, put, post, del, upload, getRefreshToken, completeSignIn, isPlatformAdmin, updateStoredUser } from "../utils/service";
import PageTabs from "@/components/layout/PageTabs";
import UserAvatar from "@/components/ui/user-avatar";
import { currencyName } from "@/components/ui/flag";
import AppSelect from "@/components/ui/app-select";

interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  timezone: string | null;
  preferred_currency: string | null;
  has_password: boolean;
  avatar_url: string | null;
  created_at: string;
  last_login_at: string | null;
}

interface MeResponse {
  success: boolean;
  data: {
    user: UserProfile;
    organizations: Array<{ id: string; name: string; role: { name: string | null } }>;
    current_organization_id: string | null;
    linked_providers: Array<{ provider: string; provider_email: string | null; last_used_at: string | null }>;
  };
}

interface PreferenceRow { event: string; label: string; channels: string[] }

const PROVIDER_LABELS: Record<string, string> = { google: "Google", microsoft: "Microsoft" };
const COMMON_CURRENCIES = ["UGX", "USD", "EUR", "GBP", "KES", "TZS", "RWF"];

const apiMessage = (error: any, fallback: string) => {
  const errors = error?.response?.data?.errors as Record<string, string[]> | undefined;
  return (errors && Object.values(errors).flat()[0]) || error?.response?.data?.message || fallback;
};

const Toggle = ({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) => (
  <label className="relative inline-flex items-center cursor-pointer">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
  </label>
);

const inputClass = "w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent";

const SkeletonCard = () => (
  <div className="animate-pulse bg-white rounded-lg p-6">
    <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
    <div className="space-y-3">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="h-10 bg-gray-200 rounded"></div>
      ))}
    </div>
  </div>
);

function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [roleName, setRoleName] = useState("");
  const [currencies, setCurrencies] = useState<string[]>(COMMON_CURRENCIES);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const avatarInput = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || 'basic');
  const [linked, setLinked] = useState<MeResponse["data"]["linked_providers"]>([]);
  const [providers, setProviders] = useState<string[]>([]);
  const [preferences, setPreferences] = useState<PreferenceRow[]>([]);
  const [busyProvider, setBusyProvider] = useState<string | null>(null);
  
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  const loadMe = useCallback(async () => {
    const me = await get<MeResponse>('/auth/me');
    setProfile(me.data.user);
    setLinked(me.data.linked_providers);
    const current = me.data.organizations.find((o) => o.id === me.data.current_organization_id);
    setRoleName(current?.role.name ?? "");
  }, []);

  useEffect(() => {
    const fetchSettingsData = async () => {
      setLoading(true);
      try {
        await loadMe();
        const [providersRes, prefsRes, currenciesRes] = await Promise.allSettled([
          get<{ data: { providers: string[] } }>('/auth/oauth/providers'),
          isPlatformAdmin() ? Promise.reject(new Error('organization only')) : get<{ data: { events: PreferenceRow[] } }>('/notification-preferences'),
          get<{ data: Array<{ currency: string }> }>('/exchange/currencies/flags'),
        ]);
        if (providersRes.status === 'fulfilled') setProviders(providersRes.value.data.providers);
        if (prefsRes.status === 'fulfilled') setPreferences(prefsRes.value.data.events);
        if (currenciesRes.status === 'fulfilled' && currenciesRes.value.data.length) {
          // Common currencies first, then every other one alphabetically.
          const all = [...new Set(currenciesRes.value.data.map((c) => c.currency))].sort();
          setCurrencies([...COMMON_CURRENCIES, ...all.filter((c) => !COMMON_CURRENCIES.includes(c))]);
        }
      } catch (error) {
        notify(apiMessage(error, 'Could not load your settings.'));
      } finally {
        setLoading(false);
      }
    };
    fetchSettingsData();
  }, [loadMe]);

  // Coming back from connecting Google / Microsoft.
  useEffect(() => {
    const linkedProvider = searchParams.get('linked');
    if (linkedProvider) {
      notify(`${PROVIDER_LABELS[linkedProvider] ?? linkedProvider} sign-in is now turned on. You can use it next time you sign in.`, { tone: 'success' });
      setActiveTab('signin');
      setSearchParams({ tab: 'signin' }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const updateProfileField = (field: keyof UserProfile, value: string) => {
    if (profile) {
      setProfile(prev => prev ? { ...prev, [field]: value } : null);
    }
  };

  const saveProfileChanges = async () => {
    if (!profile) return;

    setSaving(true);
    try {
      await put('/auth/profile', {
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone || null,
        timezone: profile.timezone || null,
        preferred_currency: profile.preferred_currency || undefined,
      });
      updateStoredUser({ first_name: profile.first_name, last_name: profile.last_name, phone: profile.phone ?? '', preferred_currency: profile.preferred_currency ?? 'USD' });
      notify('Profile updated');
    } catch (error) {
      notify(apiMessage(error, 'Could not update your profile.'));
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwords.new !== passwords.confirm) {
      notify("The new passwords don't match.");
      return;
    }

    try {
      const response = await put<{ success: boolean; data: { token: string; refresh_token: string } }>('/auth/password', {
        current_password: profile?.has_password ? passwords.current : undefined,
        new_password: passwords.new,
        refresh_token: getRefreshToken(),
      });
      // Other sessions were signed out; this device gets a fresh session.
      await completeSignIn(response.data, localStorage.getItem('refresh_token') !== null);
      notify(profile?.has_password ? 'Password changed. Other devices have been signed out.' : 'Password set. You can now sign in with your email and password too.');
      setPasswords({ current: '', new: '', confirm: '' });
      await loadMe();
    } catch (error) {
      notify(apiMessage(error, 'Could not update your password.'));
    }
  };

  const connectProvider = async (provider: string) => {
    setBusyProvider(provider);
    try {
      const r = await post<{ data: { url: string } }>(`/auth/oauth/${provider}/link`, {});
      window.location.href = r.data.url;
    } catch (error) {
      notify(apiMessage(error, 'Could not start the connection.'));
      setBusyProvider(null);
    }
  };

  const disconnectProvider = async (provider: string) => {
    if (!await confirmDialog(`Turn off ${PROVIDER_LABELS[provider] ?? provider} sign-in? You can connect it again any time.`)) return;
    setBusyProvider(provider);
    try {
      await del(`/auth/oauth/${provider}`);
      notify(`${PROVIDER_LABELS[provider] ?? provider} sign-in turned off`);
      await loadMe();
    } catch (error) {
      notify(apiMessage(error, 'Could not turn it off.'));
    } finally {
      setBusyProvider(null);
    }
  };

  const toggleChannel = (event: string, channel: string, on: boolean) => {
    setPreferences((rows) => rows.map((r) => r.event !== event ? r : { ...r, channels: on ? [...new Set([...r.channels, channel])] : r.channels.filter((c) => c !== channel) }));
  };

  const handleNotificationUpdate = async () => {
    try {
      const r = await put<{ data: { events: PreferenceRow[] } }>('/notification-preferences', { events: preferences.map((p) => ({ event: p.event, channels: p.channels })) });
      setPreferences(r.data.events);
      notify('Notification preferences saved');
    } catch (error) {
      notify(apiMessage(error, 'Could not save your preferences.'));
    }
  };

  const changeAvatar = async (file: File | undefined) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) { notify('Choose a PNG, JPG or WebP image.'); return; }
    if (file.size > 2 * 1024 * 1024) { notify('Choose an image smaller than 2 MB.'); return; }
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      const r = await upload<{ data: { user: UserProfile } }>('/auth/avatar', fd);
      setProfile((p) => p ? { ...p, avatar_url: r.data.user.avatar_url } : p);
      updateStoredUser({ avatar_url: r.data.user.avatar_url });
      notify('Profile picture updated', { tone: 'success' });
    } catch (error) {
      notify(apiMessage(error, 'Could not upload the picture.'));
    } finally {
      setAvatarBusy(false);
      if (avatarInput.current) avatarInput.current.value = '';
    }
  };

  const removeAvatar = async () => {
    if (!await confirmDialog('Remove your profile picture?')) return;
    setAvatarBusy(true);
    try {
      await del('/auth/avatar');
      setProfile((p) => p ? { ...p, avatar_url: null } : p);
      updateStoredUser({ avatar_url: null });
      notify('Profile picture removed');
    } catch (error) {
      notify(apiMessage(error, 'Could not remove the picture.'));
    } finally {
      setAvatarBusy(false);
    }
  };

  const menuItems = [
    { id: 'basic', label: 'Basic Information', icon: User },
    { id: 'signin', label: 'Sign-in Methods', icon: KeyRound },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Password & Security', icon: Lock }
  ].filter((item) => !isPlatformAdmin() || item.id !== 'notifications'); // organization feature

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="w-full">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <SkeletonCard />
            </div>
            <div className="lg:col-span-3">
              <SkeletonCard />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const timezones = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <PageTabs tabs={menuItems} active={activeTab} onChange={(id) => { setActiveTab(id); setSearchParams({ tab: id }, { replace: true }); }}>
          <div className="space-y-6">
            {activeTab === 'basic' && profile && (
              <div className="space-y-6">
                <div className="bg-black rounded-lg p-6">
                  <div className="flex items-start gap-6 mb-6">
                    <div className="relative shrink-0">
                      <UserAvatar src={profile.avatar_url} name={`${profile.first_name} ${profile.last_name}`} className="h-20 w-20 text-sm" />
                      <button type="button" onClick={() => avatarInput.current?.click()} disabled={avatarBusy} title="Change picture"
                        className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border-2 border-black bg-white text-gray-900 hover:bg-gray-100 disabled:opacity-60">
                        {avatarBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                      </button>
                      <input ref={avatarInput} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => changeAvatar(e.target.files?.[0])} />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xs font-bold text-white mb-2">
                        {profile.first_name} {profile.last_name}
                      </h2>
                      <div className="flex flex-wrap gap-4 text-xs text-gray-300">
                        <div className="flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          <span>{profile.email}</span>
                        </div>
                        {profile.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-3 h-3" />
                            <span>{profile.phone}</span>
                          </div>
                        )}
                        {roleName && (
                          <div className="flex items-center gap-2">
                            <Shield className="w-3 h-3" />
                            <span>{roleName}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button type="button" onClick={() => avatarInput.current?.click()} disabled={avatarBusy}
                          className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-xs font-medium text-gray-900 hover:bg-gray-100 disabled:opacity-60">
                          <Camera className="h-3.5 w-3.5" />{profile.avatar_url ? 'Change picture' : 'Upload picture'}
                        </button>
                        {profile.avatar_url && (
                          <button type="button" onClick={removeAvatar} disabled={avatarBusy}
                            className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-white/10 disabled:opacity-60">
                            <Trash2 className="h-3.5 w-3.5" />Remove
                          </button>
                        )}
                        <span className="self-center text-[11px] text-gray-400">PNG, JPG or WebP, up to 2 MB</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6">
                  <h2 className="text-xs font-bold text-gray-900 mb-6">Basic Information</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <User className="w-3 h-3 text-gray-400" />
                        First Name
                      </label>
                      <input type="text" value={profile.first_name} onChange={(e) => updateProfileField('first_name', e.target.value)} placeholder="First Name" className={inputClass} />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <User className="w-3 h-3 text-gray-400" />
                        Last Name
                      </label>
                      <input type="text" value={profile.last_name} onChange={(e) => updateProfileField('last_name', e.target.value)} placeholder="Last Name" className={inputClass} />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <Mail className="w-3 h-3 text-gray-400" />
                        Email Address
                      </label>
                      <input type="email" value={profile.email} disabled className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg bg-gray-50 text-gray-500" />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <Phone className="w-3 h-3 text-gray-400" />
                        Phone Number
                      </label>
                      <input type="tel" value={profile.phone || ''} onChange={(e) => updateProfileField('phone', e.target.value)} placeholder="+256 700 000000" className={inputClass} />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <Globe className="w-3 h-3 text-gray-400" />
                        Time Zone
                      </label>
                      <AppSelect value={profile.timezone || ''} onChange={(e) => updateProfileField('timezone', e.target.value)} className={inputClass}>
                        <option value="">Organization default</option>
                        {timezones.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                      </AppSelect>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <Globe className="w-3 h-3 text-gray-400" />
                        Display Currency
                      </label>
                      <AppSelect value={profile.preferred_currency || 'USD'} onChange={(e) => updateProfileField('preferred_currency', e.target.value)} className={inputClass}>
                        {[...new Set([profile.preferred_currency || 'USD', ...currencies])].map((c) => <option key={c} value={c}>{`${c} · ${currencyName(c)}`}</option>)}
                      </AppSelect>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={saveProfileChanges}
                      disabled={saving}
                      className="px-6 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saving ? 'Updating...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'signin' && profile && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6">
                  <h2 className="text-xs font-bold text-gray-900 mb-2">Sign-in Methods</h2>
                  <p className="text-xs text-gray-500 mb-6">
                    Choose how you sign in. Once a method is connected, you can use either your email and password or that account.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3">
                        <Mail className="w-4 h-4 text-gray-400 mt-0.5" />
                        <div>
                          <h3 className="text-xs font-semibold text-gray-900 mb-1">Email and password</h3>
                          <p className="text-xs text-gray-500">{profile.has_password ? `Signed in as ${profile.email}.` : 'No password set yet. Set one under Password & Security.'}</p>
                        </div>
                      </div>
                      {profile.has_password ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700"><CheckCircle2 className="w-3.5 h-3.5" /> On</span>
                      ) : (
                        <button onClick={() => setActiveTab('security')} className="px-4 py-1.5 text-xs font-semibold rounded-lg border border-primary text-primary hover:bg-primary/10">Set password</button>
                      )}
                    </div>

                    {providers.length === 0 && (
                      <p className="text-xs text-gray-500 border-t border-gray-100 pt-4">Google and Microsoft sign-in are not turned on for this system yet. A system administrator can enable them.</p>
                    )}

                    {providers.map((provider) => {
                      const account = linked.find((l) => l.provider === provider);
                      const label = PROVIDER_LABELS[provider] ?? provider;
                      return (
                        <div key={provider} className="flex items-start justify-between gap-4 border-t border-gray-100 pt-4">
                          <div className="flex items-start gap-3">
                            <Link2 className="w-4 h-4 text-gray-400 mt-0.5" />
                            <div>
                              <h3 className="text-xs font-semibold text-gray-900 mb-1">{label}</h3>
                              <p className="text-xs text-gray-500">
                                {account
                                  ? `Connected${account.provider_email ? ` as ${account.provider_email}` : ''}.${account.last_used_at ? ` Last used ${new Date(account.last_used_at).toLocaleDateString()}.` : ''}`
                                  : `Sign in with your ${label} account instead of typing a password.`}
                              </p>
                            </div>
                          </div>
                          {account ? (
                            <button disabled={busyProvider === provider} onClick={() => disconnectProvider(provider)} className="px-4 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">Turn off</button>
                          ) : (
                            <button disabled={busyProvider === provider} onClick={() => connectProvider(provider)} className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50">{busyProvider === provider ? 'Opening…' : `Connect ${label}`}</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6">
                  <h2 className="text-xs font-bold text-gray-900 mb-2">Notifications</h2>
                  <p className="text-xs text-gray-500 mb-6">
                    Choose what you hear about, in the app (the bell) and by email.
                  </p>

                  <div className="space-y-4">
                    <div className="hidden sm:flex justify-end gap-10 pr-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      <span>In app</span><span>Email</span>
                    </div>
                    {preferences.map((pref) => (
                      <div key={pref.event} className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-xs font-semibold text-gray-900 mb-1">{pref.label}</h3>
                        </div>
                        <div className="flex items-center gap-6">
                          <Toggle checked={pref.channels.includes('in_app')} onChange={(v) => toggleChannel(pref.event, 'in_app', v)} />
                          <Toggle checked={pref.channels.includes('mail')} onChange={(v) => toggleChannel(pref.event, 'mail', v)} />
                        </div>
                      </div>
                    ))}
                    {preferences.length === 0 && <p className="text-xs text-gray-500">Notification preferences are available once you belong to an organization.</p>}
                  </div>

                  <div className="flex justify-end mt-6">
                    <button
                      onClick={handleNotificationUpdate}
                      disabled={preferences.length === 0}
                      className="px-6 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'security' && profile && (
              <div className="bg-white rounded-lg p-6">
                <h2 className="text-xs font-bold text-gray-900 mb-2">{profile.has_password ? 'Change Password' : 'Set a Password'}</h2>
                <p className="text-xs text-gray-500 mb-6">
                  {profile.has_password
                    ? 'Use at least 10 characters with upper and lower case letters and a number. Changing it signs you out on other devices.'
                    : 'You currently sign in with Google or Microsoft. Setting a password lets you also sign in with your email.'}
                </p>

                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {profile.has_password && (
                      <div className="relative">
                        <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                          <Lock className="w-3 h-3 text-gray-400" />
                          Current Password
                        </label>
                        <input
                          type={showCurrentPassword ? "text" : "password"}
                          value={passwords.current}
                          autoComplete="current-password"
                          onChange={(e) => setPasswords(prev => ({ ...prev, current: e.target.value }))}
                          className={`${inputClass} pr-10`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
                        >
                          {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    )}

                    <div className="relative">
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <Lock className="w-3 h-3 text-gray-400" />
                        New password
                      </label>
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={passwords.new}
                        autoComplete="new-password"
                        onChange={(e) => setPasswords(prev => ({ ...prev, new: e.target.value }))}
                        className={`${inputClass} pr-10`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="relative">
                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                      <Lock className="w-3 h-3 text-gray-400" />
                      Confirm new password
                    </label>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={passwords.confirm}
                      autoComplete="new-password"
                      onChange={(e) => setPasswords(prev => ({ ...prev, confirm: e.target.value }))}
                      className={`${inputClass} pr-10`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handlePasswordChange}
                    disabled={(profile.has_password && !passwords.current) || !passwords.new || !passwords.confirm}
                    className="px-6 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {profile.has_password ? 'Change Password' : 'Set Password'}
                  </button>
                </div>
              </div>
            )}
          </div>
      </PageTabs>
    </div>
  );
}

export default SettingsPage;
